/**
 * Document model for quotes and invoices.
 *
 * Pure — no React, no data access — so the legally sensitive parts (VAT summary,
 * totals, what has to appear on the paper) are unit-tested.
 *
 * The layout follows the minimum content of §11 UStG (AT) / §14 UStG (DE):
 * issuer with VAT id, recipient, sequential number, invoice date, period of supply,
 * quantity and description per line, and — the part people forget — the net amount,
 * rate and tax amount **grouped per VAT rate**.
 */

import type {
  Invoice, InvoiceItem, ISODate, Quote, Tenant, Guest, Unit, Property, Booking,
} from '../data/model'
import { fromCents, toCents } from './money'

export type DocKind = 'quote' | 'invoice' | 'credit_note'
export type DocLang = 'de' | 'en'

export interface DocParty {
  name: string
  lines: string[]
  vatId?: string
  email?: string
  phone?: string
  /** Logo des Betriebs, nur beim Absender. Leer, solange keines hochgeladen wurde. */
  logo?: string | null
}

export interface DocLine {
  label: string
  qty: number
  unitPrice: number
  net: number
  vatRate: number
  vat: number
  gross: number
}

/** One row of the tax summary — one per distinct VAT rate used in the document. */
export interface VatBucket {
  rate: number
  net: number
  vat: number
  gross: number
}

export interface DocPayment {
  /** Amount due now, when it differs from the total (prepayment/Anzahlung). */
  prepayment: number
  prepaymentDue?: ISODate | null
  /** Refundable security deposit — never part of the totals, shown separately. */
  deposit: number
  paid: number
  outstanding: number
  /**
   * Wann und wie zuletzt bezahlt wurde. Erst damit taugt der Beleg als Quittung:
   * „Betrag dankend erhalten am 27.07.2026, bar" ist etwas anderes als eine
   * Rechnung mit offenem Betrag, und der Gast braucht genau das zweite nicht mehr,
   * sobald er bezahlt hat.
   */
  paidOn?: ISODate | null
  paidMethod?: string | null
  iban?: string
  bic?: string
  accountHolder?: string
  reference: string
}

export interface DocModel {
  kind: DocKind
  /** Free text above the lines. A credit note carries its Storno reason here. */
  intro?: string | null
  /**
   * Stornobedingungen, wie sie beim Erzeugen des Angebots galten. Leer heißt
   * „nichts vereinbart" — der Beleg schreibt dann den Verweis auf die gesetzlichen
   * Bestimmungen und erfindet keine Klausel.
   */
  terms?: string | null
  lang: DocLang
  number: string
  /**
   * Ist das noch ein Entwurf? Eine Rechnung ohne `issued_at` trägt keine Nummer
   * und darf nicht als Papier hinausgehen.
   *
   * WARUM EIN EIGENES FELD UND NICHT `number === 'ENTWURF'`: das Wort steht in
   * `DRAFT_NUMBER` und ist übersetzt — auf Englisch heißt es „DRAFT". Ein
   * lokalisiertes Anzeigewort als Steuerflag zu benutzen heißt, dass eine
   * Änderung am Wortlaut still eine Sperre aufhebt, die eine Rechnung ohne
   * Nummer aus dem Verkehr hält. Deshalb hier ein Feld, das nichts anzeigt.
   */
  entwurf: boolean
  issueDate: ISODate
  dueDate?: ISODate | null
  validUntil?: ISODate | null
  issuer: DocParty
  recipient: DocParty
  stay?: {
    unit: string
    property?: string
    arrival: ISODate
    departure: ISODate
    nights: number
    adults: number
    children: number
  }
  lines: DocLine[]
  vatBuckets: VatBucket[]
  totals: { net: number; vat: number; gross: number }
  payment: DocPayment
  /** Set when the issuer does not charge VAT — the note is legally required. */
  vatExemptNote: boolean
  currency: string
  notes?: string
}

/**
 * Groups lines by VAT rate. Buckets are summed in cents and only then converted
 * back, so the summary always reconciles with the document total to the cent.
 */
export function vatSummary(lines: DocLine[]): VatBucket[] {
  const byRate = new Map<number, { net: number; vat: number }>()
  for (const l of lines) {
    const cur = byRate.get(l.vatRate) ?? { net: 0, vat: 0 }
    cur.net += toCents(l.net)
    cur.vat += toCents(l.vat)
    byRate.set(l.vatRate, cur)
  }
  return [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, v]) => ({
      rate,
      net: fromCents(v.net),
      vat: fromCents(v.vat),
      gross: fromCents(v.net + v.vat),
    }))
}

function issuerOf(t: Tenant): DocParty {
  return {
    name: t.legal_name || t.name,
    lines: [t.address ?? ''].filter(Boolean),
    vatId: t.vat_id,
    email: t.email,
    phone: t.phone,
    logo: t.logo_url ?? null,
  }
}

function recipientOf(g: Guest | undefined): DocParty {
  if (!g) return { name: '—', lines: [] }
  const name = [g.first_name, g.last_name].filter(Boolean).join(' ')
  return {
    name: g.company || name || g.email || '—',
    lines: [
      g.company && name ? name : '',
      g.address ?? '',
      [g.postcode, g.city].filter(Boolean).join(' '),
      g.country ?? '',
    ].filter(Boolean),
    email: g.email,
  }
}

/**
 * Wie die Datei heißt, wenn der Beleg als PDF gespeichert wird.
 *
 * Der Browser nimmt dafür den Titel des Dokuments — und der war bisher „StaySuite",
 * also hieß jede gespeicherte Rechnung gleich. Wer im Ordner „Belege 2026" liegt,
 * braucht dort aber die Belegart, die Nummer und das Datum, sonst ist die zweite
 * Datei „StaySuite (1).pdf" und niemand weiß mehr, was drinsteht.
 *
 * Unterstriche statt Leerzeichen: die Datei geht als Mailanhang weiter und landet in
 * fremden Buchhaltungen. Alles, was ein Dateisystem oder eine URL stört, fliegt raus —
 * ein Schrägstrich im Namen erzeugt sonst je nach System einen Ordner oder gar nichts.
 */
export function documentFileName(model: Pick<DocModel, 'kind' | 'lang' | 'number' | 'issueDate'>): string {
  const label = ({
    de: { quote: 'Angebot', invoice: 'Rechnung', credit_note: 'Gutschrift' },
    en: { quote: 'Quote', invoice: 'Invoice', credit_note: 'Credit-note' },
  } as const)[model.lang][model.kind]

  return [label, model.number, model.issueDate]
    .filter(Boolean)
    .join('_')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
}

/**
 * Kürzt nachlaufende Nullen: 3 statt 3,00 — aber 2,5 bleibt 2,5.
 *
 * Steht hier und nicht in den Ansichten, weil dieselbe Zahl auf demselben Beleg
 * am Bildschirm und im PDF gleich aussehen muss. Zwei Kopien derselben Funktion
 * sind zwei Gelegenheiten, dass sie es nicht mehr tut.
 */
export function docQty(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100)
}

export interface BuildArgs {
  tenant: Tenant
  guest?: Guest
  booking?: Booking
  unit?: Unit
  property?: Property
  paid?: number
  /** Bereits geleistete Anzahlung, getrennt ausgewiesen. */
  prepaid?: number
  /** Tag und Weg der letzten Zahlung — macht aus der Rechnung eine Quittung. */
  paidOn?: ISODate | null
  paidMethod?: string | null
}

/** What stands where the number goes until the invoice is issued. */
export const DRAFT_NUMBER = { de: 'ENTWURF', en: 'DRAFT' } as const

export function buildInvoiceDoc(
  invoice: Invoice, items: InvoiceItem[], args: BuildArgs,
): DocModel {
  const lines: DocLine[] = [...items]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((i) => ({
      label: i.label,
      qty: i.qty,
      unitPrice: i.unit_price,
      net: i.amount_net,
      vatRate: i.vat_rate,
      vat: i.amount_vat,
      gross: i.amount_gross,
    }))

  const paid = args.paid ?? invoice.paid_amount
  // Die Anzahlung getrennt ausweisen statt sie in „bereits bezahlt" zu verstecken:
  // der Gast hat sie bei der Buchung geleistet und will sie auf der Rechnung
  // wiederfinden — sonst wirkt der Betrag, als würde er zweimal verlangt.
  const prepaid = args.prepaid ?? 0
  return {
    // A Storno prints as its own document type, not as a negative invoice with an
    // invoice's title — the guest has to be able to tell them apart at a glance.
    kind: invoice.type === 'credit_note' ? 'credit_note' : 'invoice',
    lang: (invoice.language === 'en' ? 'en' : 'de'),
    // A draft has no number yet and says so, rather than printing an empty line
    // where the guest expects one.
    number: invoice.number ?? DRAFT_NUMBER[invoice.language === 'en' ? 'en' : 'de'],
    // Nicht `!invoice.number`: die Nummer ist die Folge des Festschreibens, das
    // Festschreiben selbst steht in `issued_at`. Wer beides prüft, prüft zweimal
    // dasselbe und übersieht den Fall, in dem eines davon fehlt.
    entwurf: !invoice.issued_at,
    intro: invoice.intro_text ?? null,
    issueDate: invoice.issue_date,
    dueDate: invoice.due_date ?? null,
    issuer: issuerOf(args.tenant),
    recipient: recipientOf(args.guest),
    stay: stayOf(args),
    lines,
    vatBuckets: vatSummary(lines),
    totals: {
      net: invoice.total_net, vat: invoice.total_vat, gross: invoice.total_gross,
    },
    payment: {
      prepayment: prepaid,
      deposit: args.booking?.deposit_amount ?? 0,
      paid,
      outstanding: round2(invoice.total_gross - paid),
      paidOn: args.paidOn ?? null,
      paidMethod: args.paidMethod ?? null,
      iban: args.tenant.bank_iban,
      bic: args.tenant.bank_bic,
      accountHolder: args.tenant.bank_holder,
      reference: invoice.number ?? '',
    },
    vatExemptNote: args.tenant.vat_scheme !== 'standard',
    currency: invoice.currency,
  }
}

export function buildQuoteDoc(quote: Quote, lines: DocLine[], args: BuildArgs): DocModel {
  return {
    kind: 'quote',
    lang: (quote.language === 'en' ? 'en' : 'de'),
    number: quote.number,
    // Ein Angebot hat nie ein `issued_at` und ist trotzdem nie ein Entwurf: es
    // trägt von Anfang an seine Nummer. Die Entwurfssperre gilt der Rechnung,
    // nicht dem Beleg an sich.
    entwurf: false,
    terms: quote.terms_text ?? null,
    issueDate: quote.created_at.slice(0, 10),
    validUntil: quote.valid_until ?? null,
    issuer: issuerOf(args.tenant),
    recipient: recipientOf(args.guest),
    stay: stayOf(args),
    lines,
    vatBuckets: vatSummary(lines),
    totals: { net: quote.total_net, vat: quote.total_vat, gross: quote.total_gross },
    payment: {
      prepayment: quote.prepayment_amount,
      prepaymentDue: args.booking?.prepayment_due ?? null,
      deposit: quote.deposit_amount,
      paid: 0,
      outstanding: quote.total_gross,
      iban: args.tenant.bank_iban,
      bic: args.tenant.bank_bic,
      accountHolder: args.tenant.bank_holder,
      reference: quote.number,
    },
    vatExemptNote: args.tenant.vat_scheme !== 'standard',
    currency: quote.currency,
  }
}

function stayOf(args: BuildArgs): DocModel['stay'] {
  const b = args.booking
  if (!b || !args.unit) return undefined
  const nights = Math.round(
    (new Date(b.departure + 'T00:00:00').getTime()
      - new Date(b.arrival + 'T00:00:00').getTime()) / 86_400_000,
  )
  return {
    unit: args.unit.name,
    property: args.property?.name,
    arrival: b.arrival,
    departure: b.departure,
    nights,
    adults: b.adults,
    children: b.children,
  }
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}
